// FilterTags.tsx - Consumer repo version

import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface FilterTag {
  category: string;
  value: string;
  displayCategory: string;
}

interface FilterTagsProps {
  // Selected filter values (excluding applications)
  colourList: string[];
  designList: string[];
  typesList: string[];
  brandList: string[];
  materialList: string[];
  standardsList: string[];
  sizeList: string[];
  shapeList: string[];

  // Functions
  onRemoveFilter: (category: string, value: string) => void;
  onClearAll: () => void;
}

const FilterTags: React.FC<FilterTagsProps> = ({
                                                 colourList,
                                                 designList,
                                                 typesList,
                                                 brandList,
                                                 materialList,
                                                 standardsList,
                                                 sizeList,
                                                 shapeList,
                                                 onRemoveFilter,
                                                 onClearAll
                                               }) => {

  const filterTags: FilterTag[] = [
    ...brandList.map(value => ({
      category: 'brand',
      value,
      displayCategory: 'Brand'
    })),
    ...designList.map(value => ({
      category: 'design_style',
      value,
      displayCategory: 'Design'
    })),
    ...standardsList.map(value => ({
      category: 'standards',
      value,
      displayCategory: 'Standard'
    })),
    ...colourList.map(value => ({
      category: 'colours',
      value,
      displayCategory: 'Colour'
    })),
    ...typesList.map(value => ({
      category: 'types',
      value,
      displayCategory: 'Type'
    })),
    ...materialList.map(value => ({
      category: 'material',
      value,
      displayCategory: 'Material'
    })),
    ...sizeList.map(value => ({
      category: 'sizes',
      value,
      displayCategory: 'Size'
    })),
    ...shapeList.map(value => ({
      category: 'shapes',
      value,
      displayCategory: 'Shape'
    }))
  ];

  // Don't render if no filters selected
  if (filterTags.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, mt: 2 }}>
      {/* Filter Tags with Clear All inline */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center',
        maxHeight: { xs: '150px', sm: '120px' },
        overflowY: 'auto'
      }}>
        {/* Clear All Button as first item */}
        <Typography
          onClick={onClearAll}
          sx={{
            color: '#333',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            padding: '6px 12px',
            border: '1px solid #ddd',
            borderRadius: '20px',
            backgroundColor: '#f9f9f9',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#e0e0e0',
              textDecoration: 'underline'
            }
          }}
        >
          Clear All
        </Typography>

        {/* Filter Tags */}
        {filterTags.map((tag, index) => (
          <Chip
            key={`${tag.category}-${tag.value}-${index}`}
            label={tag.value}
            onDelete={() => onRemoveFilter(tag.category, tag.value)}
            deleteIcon={<CloseIcon />}
            variant="outlined"
            size="medium"
            sx={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '20px',
              fontSize: '14px',
              height: '32px',
              maxWidth: '200px',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#333'
              },
              '& .MuiChip-deleteIcon': {
                fontSize: '18px',
                color: '#666',
                '&:hover': {
                  color: '#333'
                }
              },
              '&:hover': {
                backgroundColor: '#eeeeee'
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default FilterTags;